import { DestroyRef, Injectable, NgZone, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BannersConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { TickerInterval, TickerService } from '@frontend/sports/common/core/utils/timer';
import { fromSportsEnter, fromSportsLeave } from '@frontend/sports/host-app/sports-product/feature/utils';
import { isEmpty } from 'lodash-es';
import { Subject, windowCount } from 'rxjs';

import { JackpotEvent, JackpotIncrementEvent, JackpotRefreshEvent } from './banner.model';
import { BannersService } from './banners.service';

@Injectable({ providedIn: 'root' })
export class JackpotService implements OnDestroy {
    private eventsSubject = new Subject<JackpotEvent>();
    private tick = new Subject<void>();

    readonly events = this.eventsSubject.asObservable();
    jackpotGroups: StringDictionary<number> = {};

    private readonly logger: SportsRemoteLogger;

    constructor(
        private bannersService: BannersService,
        private bannerConfig: BannersConfig,
        private timer: TickerService,
        loggerFactory: LoggerFactory,
        private zone: NgZone,
        destroyRef: DestroyRef,
    ) {
        this.initTimer();
        fromSportsEnter()
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe(() => this.initTimer());
        fromSportsLeave()
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe(() => this.deInitTimer());

        this.logger = loggerFactory.getLogger('JackpotService');
    }

    private initTimer() {
        this.deInitTimer();

        this.timer.subscribe({ id: 'jackpotBanner', onTick: () => this.tick.next(), interval: TickerInterval.Second });

        this.tick.pipe(windowCount(this.bannerConfig.jackpot.incrementTime)).subscribe(this.onIncrement);
        this.tick.pipe(windowCount(this.bannerConfig.jackpot.refreshTime)).subscribe(this.onRefresh);
    }

    private deInitTimer() {
        this.timer.unsubscribe('jackpotBanner');
    }

    private onRefresh = async () => {
        const jackpots = Object.keys(this.jackpotGroups);
        if (!jackpots.length) {
            return;
        }

        try {
            const response = await this.bannersService.getJackpotValues(jackpots);
            this.zone.run(() => this.eventsSubject.next(new JackpotRefreshEvent(response.values)));
        } catch (error) {
            this.logger.error(error, 'Failed to get jackpot values.');
        }
    };

    private onIncrement = () => {
        if (isEmpty(this.jackpotGroups)) {
            return;
        }

        this.zone.run(() => this.eventsSubject.next(new JackpotIncrementEvent(this.bannerConfig.jackpot.incrementValue)));
    };

    ngOnDestroy(): void {
        this.deInitTimer();
    }

    registerGroup(jackpotGroup: string): void {
        if (this.jackpotGroups[jackpotGroup] === undefined) {
            this.jackpotGroups[jackpotGroup] = 1;
        } else {
            this.jackpotGroups[jackpotGroup]++;
        }
    }

    unregisterGroup(jackpotGroup: string): void {
        if (this.jackpotGroups[jackpotGroup] === 1) {
            delete this.jackpotGroups[jackpotGroup];
        } else {
            this.jackpotGroups[jackpotGroup]--;
        }
    }
}
